"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Phone, MessageSquare, Sparkles, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDef {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: Array<{
    value: string;
    label: string;
  }>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id?: number;
  key: string;
  name: string;
  fields: FormFieldDef[];
  submit?: {
    label?: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
  };
  confirmation?: {
    type: string;
    message: string;
    url?: string;
  };
}
export default function Trial() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");

  // Form states
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [formLoading, setFormLoading] = useState<boolean>(true);
  const [formLoadError, setFormLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submittedSuccessfully, setSubmittedSuccessfully] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const fetchTimeRef = useRef<number>(Date.now());

  // Language management
  useEffect(() => {
    const getStoredLang = (): "vi" | "en" => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        return stored === "en" ? "en" : "vi";
      } catch {
        return "vi";
      }
    };
    setLang(getStoredLang());
    const handleLanguageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      if (customEvent.detail?.lang && (customEvent.detail.lang === "vi" || customEvent.detail.lang === "en")) {
        setLang(customEvent.detail.lang);
      } else {
        setLang(getStoredLang());
      }
    };
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "roboto_lang") {
        setLang(event.newValue === "en" ? "en" : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleLanguageChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLanguageChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Fetch form definition
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setFormLoadError("Forms integration is initializing...");
      setFormLoading(false);
      return;
    }
    setFormLoading(true);
    setFormLoadError(null);
    client.pluginRequest("wvc-forms/v1/forms/dang_ky_tu_van").then((data: FormDefinition) => {
      if (!isMounted) return;
      setFormDef(data);
      fetchTimeRef.current = Date.now();
      // Initialize default empty values
      const initVals: Record<string, string> = {};
      if (data.fields) {
        data.fields.forEach(f => {
          initVals[f.id] = "";
        });
      }
      setValues(initVals);
      setFormLoading(false);
    }).catch((err: any) => {
      // Recovery attempt if 404
      if (err?.status === 404) {
        client.pluginRequest("wvc-forms/v1/forms").then((listData: {
          items?: Array<{
            key: string;
            name: string;
          }>;
        }) => {
          if (!isMounted) return;
          const items = listData?.items || [];
          const matched = items.find(item => item.key === "dang_ky_tu_van") || (items.length === 1 ? items[0] : null) || items.find(item => item.name?.toLowerCase().includes("tư vấn") || item.name?.toLowerCase().includes("consult"));
          if (matched) {
            return client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`).then((recoveryData: FormDefinition) => {
              if (!isMounted) return;
              setFormDef(recoveryData);
              fetchTimeRef.current = Date.now();
              const initVals: Record<string, string> = {};
              recoveryData.fields?.forEach(f => {
                initVals[f.id] = "";
              });
              setValues(initVals);
              setFormLoading(false);
            });
          } else {
            setFormLoadError(lang === "vi" ? "Không tìm thấy biểu mẫu đăng ký." : "Consultation form not found.");
            setFormLoading(false);
          }
        }).catch(() => {
          if (!isMounted) return;
          setFormLoadError(lang === "vi" ? "Không thể tải biểu mẫu." : "Unable to load form.");
          setFormLoading(false);
        });
      } else {
        if (!isMounted) return;
        setFormLoadError(lang === "vi" ? "Lỗi tải biểu mẫu. Vui lòng tải lại trang." : "Error loading form. Please refresh.");
        setFormLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [lang]);
  const handleInputChange = (fieldId: string, val: string) => {
    setValues(prev => ({
      ...prev,
      [fieldId]: val
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const updated = {
          ...prev
        };
        delete updated[fieldId];
        return updated;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || submitting) return;
    setFormError(null);
    setFieldErrors({});

    // Min seconds anti-bot gate
    const minSeconds = formDef.spam?.min_seconds || 3;
    const elapsedSeconds = (Date.now() - fetchTimeRef.current) / 1000;
    if (elapsedSeconds < minSeconds) {
      const waitTime = Math.ceil((minSeconds - elapsedSeconds) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    setSubmitting(true);
    const client = getWvcClient();
    try {
      const payload: Record<string, any> = {
        values,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        setSubmittedSuccessfully(true);
        setSuccessMessage(lang === "vi" ? "Cảm ơn quý phụ huynh! Đội ngũ ROBOTO sẽ liên hệ tư vấn trong thời gian sớm nhất." : "Thank you parents! The ROBOTO team will reach out for consultation shortly.");
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setFormError(res.message || (lang === "vi" ? "Vui lòng kiểm tra lại các trường được đánh dấu đỏ." : "Please check the highlighted fields."));
      } else if (res?.code === "frms_invalid_token") {
        // Re-fetch token and inform user
        const refreshed = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}`);
        setFormDef(refreshed);
        fetchTimeRef.current = Date.now();
        setFormError(lang === "vi" ? "Phiên làm việc đã hết hạn. Vui lòng bấm gửi lại." : "Session expired. Please click submit again.");
      } else {
        setFormError(res?.message || (lang === "vi" ? "Có lỗi xảy ra khi gửi thông tin. Vui lòng thử lại." : "An error occurred while submitting. Please try again."));
      }
    } catch (err: any) {
      setFormError(err?.message || (lang === "vi" ? "Không thể kết nối đến máy chủ. Vui lòng thử lại sau." : "Unable to connect to server. Please try again later."));
    } finally {
      setSubmitting(false);
    }
  };

  // Find fields by key or fallback gracefully
  const parentNameField = formDef?.fields?.find(f => f.key === "parent_name");
  const phoneField = formDef?.fields?.find(f => f.key === "phone");
  const studentAgeField = formDef?.fields?.find(f => f.key === "student_age");
  const programInterestField = formDef?.fields?.find(f => f.key === "program_interest");
  const preferredCenterField = formDef?.fields?.find(f => f.key === "preferred_center");

  // Fallback field list if keys don't exactly match custom keys
  const renderedFields = [parentNameField, phoneField, studentAgeField, programInterestField, preferredCenterField].filter(Boolean) as FormFieldDef[];
  const remainingFields = formDef?.fields?.filter(f => f.type !== "heading" && f.type !== "paragraph" && f.type !== "hidden" && !renderedFields.some(rf => rf.id === f.id)) || [];
  const allActiveFields = [...renderedFields, ...remainingFields];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="dang-ky-tu-van" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {/* Background imagery with subtle light scrim gradient & calibrated soft atmospheric glow */}
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_testing_robots_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_testing_robots_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/students_testing_robots_lab.png-wvc-sizes" alt="Students testing completed robotics builds in an engineering lab" loading="lazy" decoding="async" className="h-full w-full object-cover object-center opacity-15 sm:opacity-20 contrast-105" />

        {/* Directional scrim in light background hue preserving high contrast and clarity */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />

        {/* Soft, low-opacity atmospheric glows for clean aesthetic on light background */}
        <div aria-hidden="true" className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-[520px] w-[860px] max-w-full bg-[radial-gradient(ellipse_at_center,hsl(4_72%_45%/0.08)_0%,hsl(32_92%_54%/0.05)_45%,transparent_75%)] blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute top-1/4 -left-24 h-[520px] w-[520px] bg-[radial-gradient(circle_at_center,hsl(88_68%_42%/0.07)_0%,transparent_70%)] blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -right-20 h-[560px] w-[560px] bg-[radial-gradient(circle_at_center,hsl(32_92%_54%/0.08)_0%,hsl(4_72%_45%/0.04)_50%,transparent_70%)] blur-3xl" />

        {/* Lab Blueprint precision coordinate grid overlay */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(45,20,80,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(45,20,80,0.03)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_70%_70%_at_50%_45%,#000_40%,transparent_100%)]" />
      </div>

      <div className="relative z-10 mx-auto max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mx-auto max-w-3xl text-center">
          {/* Eyebrow badge */}
          <motion.div variants={itemVariants} className="mb-5 inline-flex items-center gap-2 rounded-lg border border-secondary/40 bg-secondary/10 px-3.5 py-1 text-xs font-semibold text-secondary">
            <Sparkles className="size-3.5 text-secondary" />
            <span className="font-mono tracking-wider uppercase">
              {lang === "vi" ? "Tư Vấn Cá Nhân Hóa 1:1" : "1:1 Personalized Consultation"}
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h2 variants={itemVariants} className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {lang === "vi" ? "Bạn đang tìm chương trình Robotics phù hợp cho con?" : "Looking for the right robotics program for your child?"}
          </motion.h2>

          {/* Supporting text */}
          <motion.p variants={itemVariants} className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
            {lang === "vi" ? "Để lại thông tin, đội ngũ ROBOTO sẽ tư vấn lộ trình phù hợp với độ tuổi và nhu cầu học tập của con." : "Leave your contact details, and the ROBOTO team will advise a learning pathway tailored to your child's age and interests."}
          </motion.p>
        </motion.div>

        {/* Consultation Form Card Container with ambient backlight */}
        <div className="relative mx-auto mt-10 max-w-2xl">
          <div aria-hidden="true" className="pointer-events-none absolute -inset-1 rounded-2xl bg-gradient-to-r from-primary/10 via-accent/15 to-secondary/10 opacity-70 blur-xl" />

          <motion.div variants={itemVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="relative rounded-xl border border-border/80 bg-card/95 p-6 shadow-xl backdrop-blur-md sm:p-8 md:p-10">
            {formLoading ? <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-center">
                <Loader2 className="size-8 animate-spin text-secondary" />
                <p className="text-sm text-muted-foreground">
                  {lang === "vi" ? "Đang tải biểu mẫu tư vấn..." : "Loading consultation form..."}
                </p>
              </div> : formLoadError && !formDef ? <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 text-center">
                <AlertCircle className="size-10 text-destructive" />
                <p className="text-base text-foreground font-medium">{formLoadError}</p>
                <Button variant="outline" onClick={() => window.location.reload()} className="mt-2 text-xs">
                  {lang === "vi" ? "Tải lại trang" : "Reload page"}
                </Button>
              </div> : submittedSuccessfully ? <div className="flex min-h-[300px] flex-col items-center justify-center py-6 text-center">
                <div className="flex size-14 items-center justify-center rounded-lg bg-secondary/15 text-secondary">
                  <CheckCircle2 className="size-8 text-secondary" />
                </div>
                <h3 className="mt-4 font-serif text-2xl font-bold text-foreground">
                  {lang === "vi" ? "Đăng ký thành công!" : "Submission Successful!"}
                </h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-muted-foreground">
                  {successMessage}
                </p>
                <Button variant="outline" className="mt-8 rounded-lg border-border" onClick={() => {
              setSubmittedSuccessfully(false);
              setValues({});
            }}>
                  {lang === "vi" ? "Gửi yêu cầu khác" : "Submit another request"}
                </Button>
              </div> : <form onSubmit={handleSubmit} noValidate className="space-y-5">
                {/* Spam Honeypot */}
                {formDef?.spam?.honeypot?.field && <input type="text" name={formDef.spam.honeypot.field} value={hpValue} onChange={e => setHpValue(e.target.value)} tabIndex={-1} autoComplete="off" style={{
              position: "absolute",
              left: "-9999px"
            }} aria-hidden="true" />}

                {/* Form Global Error Notice */}
                {formError && <div className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/10 p-3.5 text-sm text-destructive">
                    <AlertCircle className="size-5 shrink-0" />
                    <p>{formError}</p>
                  </div>}

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {allActiveFields.map(field => {
                const isSelect = field.type === "select";
                const isFullWidth = field.key === "parent_name" || field.key === "program_interest";
                const hasError = !!fieldErrors[field.id];
                const errorMessage = fieldErrors[field.id]?.message;
                return <div key={field.id} className={isFullWidth ? "sm:col-span-2" : "col-span-1"}>
                        <label htmlFor={field.id} className="mb-1.5 block text-xs font-semibold tracking-wide text-foreground/90 uppercase">
                          {field.label}
                          {field.required && <span className="ml-1 text-accent">*</span>}
                        </label>

                        {isSelect ? <div className="relative">
                            <select id={field.id} value={values[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className={`w-full appearance-none rounded-lg border bg-background/80 px-4 py-3 text-sm text-foreground transition-colors focus:border-secondary focus:ring-1 focus:ring-secondary focus:outline-none ${hasError ? "border-destructive text-destructive" : "border-border"}`}>
                              <option value="">
                                {field.placeholder || (lang === "vi" ? "-- Vui lòng chọn --" : "-- Please select --")}
                              </option>
                              {field.options?.map(opt => <option key={opt.value} value={opt.value} className="bg-card text-foreground">
                                  {opt.label}
                                </option>)}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3.5 text-muted-foreground">
                              <svg className="size-4 fill-current" viewBox="0 0 20 20">
                                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                              </svg>
                            </div>
                          </div> : <input id={field.id} type={field.type === "phone" ? "tel" : field.type === "number" ? "number" : "text"} placeholder={field.placeholder || ""} value={values[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className={`w-full rounded-lg border bg-background/80 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 transition-colors focus:border-secondary focus:ring-1 focus:ring-secondary focus:outline-none ${hasError ? "border-destructive text-destructive" : "border-border"}`} />}

                        {hasError && <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
                            <AlertCircle className="size-3" />
                            <span>{errorMessage}</span>
                          </p>}
                      </div>;
              })}
                </div>

                {/* Submit Button */}
                <div className="pt-3">
                  <Button type="submit" disabled={submitting} size="lg" className="w-full h-12 rounded-lg bg-accent font-serif text-base font-semibold text-accent-foreground shadow-lg transition-[background-color,box-shadow,transform] duration-200 hover:bg-accent/90 hover:shadow-xl focus-visible:ring-secondary active:scale-[0.99] disabled:opacity-60">
                    {submitting ? <span className="flex items-center justify-center gap-2">
                        <Loader2 className="size-5 animate-spin" />
                        <span>{lang === "vi" ? "Đang gửi thông tin..." : "Submitting..."}</span>
                      </span> : <span className="flex items-center justify-center gap-2">
                        <Send className="size-4" />
                        <span>{lang === "vi" ? "ĐĂNG KÝ TƯ VẤN" : "REGISTER FOR CONSULTATION"}</span>
                      </span>}
                  </Button>
                </div>

                <p className="text-center text-xs text-muted-foreground/80">
                  {lang === "vi" ? "Thông tin phụ huynh cung cấp được bảo mật hoàn toàn theo chính sách riêng tư của ROBOTO." : "Parent information is kept strictly confidential according to ROBOTO's privacy policy."}
                </p>
              </form>}
          </motion.div>
        </div>

        {/* Quick Contact Options */}
        <motion.div variants={itemVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mx-auto mt-10 max-w-2xl border-t border-border/40 pt-8">
          <div className="text-center mb-5">
            <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              {lang === "vi" ? "Hoặc kết nối trực tiếp với chúng tôi" : "Or connect with us directly"}
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {/* Zalo Button (disabled / Sắp cập nhật) */}
            <Button disabled variant="outline" className="h-11 rounded-lg border-border/80 bg-card/60 px-5 text-sm text-muted-foreground cursor-not-allowed opacity-70">
              <MessageSquare className="size-4 text-muted-foreground" />
              <span>Zalo</span>
              <span className="ml-1 text-[11px] font-mono text-muted-foreground/80 rounded bg-muted/60 px-1.5 py-0.5">
                {lang === "vi" ? "Sắp cập nhật" : "Coming soon"}
              </span>
            </Button>

            {/* Messenger Button (disabled / Sắp cập nhật) */}
            <Button disabled variant="outline" className="h-11 rounded-lg border-border/80 bg-card/60 px-5 text-sm text-muted-foreground cursor-not-allowed opacity-70">
              <MessageSquare className="size-4 text-muted-foreground" />
              <span>Messenger</span>
              <span className="ml-1 text-[11px] font-mono text-muted-foreground/80 rounded bg-muted/60 px-1.5 py-0.5">
                {lang === "vi" ? "Sắp cập nhật" : "Coming soon"}
              </span>
            </Button>

            {/* Hotline Link */}
            <Button asChild variant="secondary" className="h-11 rounded-lg px-5 font-medium shadow-sm transition-[background-color,transform] hover:scale-[1.02]">
              <a href="tel:0346386986" className="flex items-center gap-2">
                <Phone className="size-4" />
                <span>
                  {lang === "vi" ? "Gọi Hotline: " : "Call Hotline: "}
                  <strong className="font-semibold">0346 386 986</strong>
                </span>
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}